/**
 * Knocks the flat background out of a logo so it can sit on colored artwork
 * (a wax seal, a themed invitation page) without a visible rectangle around it.
 *
 * Logos reach us flattened all the time even when the designer's original was
 * transparent: exported as JPEG, composited onto white, or — the case that
 * looks most confusing — saved from a viewer with the **transparency
 * checkerboard baked in as real pixels**. Rather than asking producers to
 * re-export, we drop that background at render time.
 *
 * The tones to remove are *sampled from the border itself* — one for a flat
 * matte, two for a checkerboard — rather than assumed to be a single color.
 * Keying one color would clear a checkerboard's white squares and leave its
 * grey ones, which reads as a frame that is still there; keying every light
 * pixel would eat silver artwork. Sampling does neither.
 *
 * Only the region connected to the edges is cleared, flood-filled inward, so
 * white counters inside letters survive. Anything uncertain is left alone: the
 * caller falls back to the untouched image.
 */

/** Longest edge we process. A logo is displayed small; the fill is per-pixel. */
const MAX_EDGE = 512;

/**
 * Darkest a channel may be and still read as background. Covers white, an
 * off-white JPEG matte, and the grey squares of a transparency checkerboard
 * (#cccccc = 204), while leaving mid-tone and dark artwork alone.
 */
const LIGHT_FLOOR = 196;

/**
 * Most a background pixel's channels may differ from each other. Backgrounds
 * are neutral; a pale but saturated color (cream, blush) is artwork.
 */
const NEUTRAL_SPREAD = 22;

/** Below this alpha a pixel is already transparent — nothing to knock out. */
const OPAQUE_ALPHA = 250;

/** How far a pixel may sit from a sampled border tone and still be that tone. */
const TONE_SPREAD = 14;

/** A flat matte has one tone, a checkerboard two; more than this is a gradient. */
const MAX_TONES = 3;

type Rgb = [number, number, number];

export interface MatteOptions {
  /** Darkest channel value that still counts as background. */
  lightFloor?: number;
  /** Largest allowed spread between a background pixel's channels. */
  neutralSpread?: number;
  /** How far a pixel may sit from a sampled border tone and still match it. */
  toneSpread?: number;
  /** Most distinct border tones tolerated before the image is left alone. */
  maxTones?: number;
}

/**
 * True when the pixel at `index` is opaque, light and neutral — the signature
 * of a flattened-in background rather than artwork.
 */
export function isBackgroundPixel(
  data: Uint8ClampedArray,
  index: number,
  { lightFloor = LIGHT_FLOOR, neutralSpread = NEUTRAL_SPREAD }: MatteOptions = {},
): boolean {
  if (data[index + 3] < OPAQUE_ALPHA) return false;
  const r = data[index];
  const g = data[index + 1];
  const b = data[index + 2];
  const min = Math.min(r, g, b);
  const max = Math.max(r, g, b);
  return min >= lightFloor && max - min <= neutralSpread;
}

function toneMatches(data: Uint8ClampedArray, index: number, tone: Rgb, spread: number): boolean {
  return (
    Math.abs(data[index] - tone[0]) <= spread &&
    Math.abs(data[index + 1] - tone[1]) <= spread &&
    Math.abs(data[index + 2] - tone[2]) <= spread
  );
}

/**
 * The distinct background tones actually present around the border — one for a
 * plain matte, two for a transparency checkerboard — or null when there is no
 * background to remove.
 *
 * Keying only these tones is what keeps light *artwork* safe: silver or pale
 * grey inside the logo is cleared only if that same tone is demonstrably part
 * of the surround, instead of every light pixel being fair game.
 *
 * Null for an already-transparent logo, artwork that bleeds into all four
 * corners, a logo whose own colored ground fills the frame, and a background
 * with too many tones to be a flat matte (a gradient) — all left untouched.
 */
export function sampleBorderTones(
  data: Uint8ClampedArray,
  width: number,
  height: number,
  options: MatteOptions = {},
): Rgb[] | null {
  if (width < 2 || height < 2) return null;
  const { toneSpread = TONE_SPREAD, maxTones = MAX_TONES } = options;
  const corners = [0, (width - 1) * 4, width * (height - 1) * 4, (width * height - 1) * 4];
  if (!corners.every((index) => isBackgroundPixel(data, index, options))) return null;

  const tones: Rgb[] = [];
  const consider = (x: number, y: number): boolean => {
    const index = (y * width + x) * 4;
    // Artwork touching the border is simply not a tone — it stays.
    if (!isBackgroundPixel(data, index, options)) return true;
    if (tones.some((tone) => toneMatches(data, index, tone, toneSpread))) return true;
    tones.push([data[index], data[index + 1], data[index + 2]]);
    return tones.length <= maxTones;
  };

  for (let x = 0; x < width; x += 1) {
    if (!consider(x, 0) || !consider(x, height - 1)) return null;
  }
  for (let y = 0; y < height; y += 1) {
    if (!consider(0, y) || !consider(width - 1, y)) return null;
  }
  // At least one tone always exists here: the corners passed the check above.
  return tones;
}

/**
 * Clear every pixel connected to an edge that matches one of `tones`, in place.
 * Returns the number of pixels cleared so the caller can tell a no-op from a
 * real change.
 */
export function clearSurroundingMatte(
  data: Uint8ClampedArray,
  width: number,
  height: number,
  tones: Rgb[],
  options: MatteOptions = {},
): number {
  const { toneSpread = TONE_SPREAD } = options;
  const seen = new Uint8Array(width * height);
  const stack: number[] = [];

  const push = (x: number, y: number) => {
    if (x < 0 || y < 0 || x >= width || y >= height) return;
    const pixel = y * width + x;
    if (seen[pixel]) return;
    seen[pixel] = 1;
    stack.push(pixel);
  };

  for (let x = 0; x < width; x += 1) {
    push(x, 0);
    push(x, height - 1);
  }
  for (let y = 0; y < height; y += 1) {
    push(0, y);
    push(width - 1, y);
  }

  let cleared = 0;
  while (stack.length > 0) {
    const pixel = stack.pop() as number;
    const index = pixel * 4;
    if (!isBackgroundPixel(data, index, options)) continue;
    if (!tones.some((tone) => toneMatches(data, index, tone, toneSpread))) continue;
    data[index + 3] = 0;
    cleared += 1;
    const x = pixel % width;
    const y = (pixel - x) / width;
    push(x - 1, y);
    push(x + 1, y);
    push(x, y - 1);
    push(x, y + 1);
  }
  return cleared;
}

function decodeObjectUrl(objectUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Could not decode the image'));
    image.src = objectUrl;
  });
}

/**
 * The image's pixels, drawn into a canvas we are allowed to read back.
 *
 * The bytes are fetched and decoded from a blob rather than pointed at with
 * `<img crossOrigin>`. Sharing a URL with the visible `<img>` is the trap
 * there: the browser reuses that first, non-CORS cache entry for the
 * crossOrigin request, which then has no `Access-Control-Allow-Origin`, so the
 * load fails or the canvas is tainted and every logo silently keeps its
 * background. A blob is same-origin, so nothing can taint the canvas.
 */
async function drawFromUrl(
  url: string,
  maxEdge: number,
): Promise<{
  context: CanvasRenderingContext2D;
  canvas: HTMLCanvasElement;
  width: number;
  height: number;
} | null> {
  const response = await fetch(url, { mode: 'cors', cache: 'reload' });
  if (!response.ok) return null;
  const blob = await response.blob();

  let source: ImageBitmap | HTMLImageElement;
  let objectUrl: string | null = null;
  if (typeof createImageBitmap === 'function') {
    source = await createImageBitmap(blob);
  } else {
    objectUrl = URL.createObjectURL(blob);
    source = await decodeObjectUrl(objectUrl);
  }

  try {
    const naturalWidth = 'naturalWidth' in source ? source.naturalWidth : source.width;
    const naturalHeight = 'naturalHeight' in source ? source.naturalHeight : source.height;
    const longest = Math.max(naturalWidth, naturalHeight);
    if (!longest) return null;
    const ratio = Math.min(1, maxEdge / longest);
    const width = Math.max(1, Math.round(naturalWidth * ratio));
    const height = Math.max(1, Math.round(naturalHeight * ratio));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) return null;
    // Nearest-neighbour, deliberately. Interpolated downscaling blends the
    // background's tones into each other — a two-tone checkerboard resampled
    // smoothly becomes a spread of greys, which then reads as a gradient and
    // gets left alone. Sampling needs the source's actual tones.
    context.imageSmoothingEnabled = false;
    context.drawImage(source, 0, 0, width, height);
    return { context, canvas, width, height };
  } finally {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    if ('close' in source) source.close();
  }
}

/**
 * A PNG data URL of `url` with its background knocked out, or null when there
 * is nothing to do (already transparent, no light surround) or the image
 * cannot be read (CORS, decode failure). Callers render `url` itself on null.
 */
export async function removeImageMatte(
  url: string,
  options: MatteOptions = {},
): Promise<string | null> {
  try {
    const drawn = await drawFromUrl(url, MAX_EDGE);
    if (!drawn) return null;
    const { context, canvas, width, height } = drawn;

    const imageData = context.getImageData(0, 0, width, height);
    const tones = sampleBorderTones(imageData.data, width, height, options);
    if (!tones) return null;
    if (clearSurroundingMatte(imageData.data, width, height, tones, options) === 0) return null;

    context.putImageData(imageData, 0, 0);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}
